import { APIKit } from "config/axios/index";
import store from "config/redux/index";
import { start, end } from "config/redux/slice";

import { ServerConstants, ServiceConstants } from "constant";
import { ServiceModel } from "models";
import {
  Bank,
  BankSpecificRequest,
  BankSpecificStatus,
  ReplyBankCommentRequest,
  CreateBankCommentRequest,
  BankCreditNoteRequest,
  NoteType,
} from "types";

import { handleErrorWithTokenRenewal } from "./common";

const {
  API_V1_BANK_SPECIFIC,
  API_V1_BANK_COMMENTS,
  API_V1_BANK_CREDIT_NOTES,
  API_V1_BANK_ATTACHMENTS,
} = ServerConstants;

const {
  REQUEST_BANK_SPECIFIC_GET_ID,
  REQUEST_BANK_SPECIFIC_OPTIONS_ID,
  REQUEST_BANK_SPECIFIC_AVAILABLE_STATUSES_ID,
  REQUEST_BANK_SPECIFIC_SAVE_ID,
  REQUEST_BANK_SPECIFIC_STATUS_CHANGE_ID,
  REQUEST_BANK_COMMENTS_LIST_ID,
  REQUEST_BANK_COMMENT_CREATE_ID,
  REQUEST_BANK_COMMENT_REPLY_ID,
  REQUEST_BANK_NOTES_LIST_ID,
  REQUEST_BANK_NOTE_CREATE_ID,
  REQUEST_BANK_NOTE_DELETE_ID,
  REQUEST_BANK_ATTACHMENT_LIST_ID,
  REQUEST_BANK_ATTACHMENT_UPLOAD_ID,
  REQUEST_BANK_ATTACHMENT_DELETE_ID,
  REQUEST_BANK_ATTACHMENT_SUMMARY_GET_ID,
  REQUEST_BANK_ATTACHMENT_SUMMARY_UPLOAD_ID,
} = ServiceConstants;

export function bankNotesRequestId(bankCreditRequestId: number, bank?: Bank, noteType?: NoteType) {
  return `${REQUEST_BANK_NOTES_LIST_ID}_${bankCreditRequestId}_${bank ?? "ALL"}_${noteType ?? "ALL"}`;
}

export async function bankSpecificGet(
  bank: Bank,
  bankCreditRequestId: number,
  requestId: string = REQUEST_BANK_SPECIFIC_GET_ID + bank,
) {
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(API_V1_BANK_SPECIFIC, {
      params: { bank, bankCreditRequestId },
    });
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, bankSpecificGet, [bank, bankCreditRequestId, requestId], requestId);
  }
}

export async function bankSpecificOptions(bank: Bank) {
  const requestId = REQUEST_BANK_SPECIFIC_OPTIONS_ID + bank;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_BANK_SPECIFIC}/options`, {
      params: { bank },
    });
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, bankSpecificOptions, [bank], requestId);
  }
}

export function bankSpecificAvailableStatusesRequestId(bank: Bank, bankCreditRequestId: number) {
  return `${REQUEST_BANK_SPECIFIC_AVAILABLE_STATUSES_ID}${bank}_${bankCreditRequestId}`;
}

export async function bankSpecificAvailableStatuses(bank: Bank, bankCreditRequestId: number) {
  const requestId = bankSpecificAvailableStatusesRequestId(bank, bankCreditRequestId);
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_BANK_SPECIFIC}/available-statuses`, {
      params: { bank, bankCreditRequestId },
    });
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, bankSpecificAvailableStatuses, [bank, bankCreditRequestId], requestId);
  }
}

export async function bankSpecificSave(data: BankSpecificRequest) {
  const requestId = REQUEST_BANK_SPECIFIC_SAVE_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.post(API_V1_BANK_SPECIFIC, data);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, bankSpecificSave, [data], requestId);
  }
}

export async function bankSpecificChangeStatus(id: number, status: BankSpecificStatus, bank: Bank) {
  const requestId = REQUEST_BANK_SPECIFIC_STATUS_CHANGE_ID + bank;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.patch(`${API_V1_BANK_SPECIFIC}/${id}/status/${status}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, bankSpecificChangeStatus, [id, status, bank], requestId);
  }
}

export async function bankCommentsAll(bank: Bank, bankCreditRequestId: number) {
  const requestId = REQUEST_BANK_COMMENTS_LIST_ID + bank;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(API_V1_BANK_COMMENTS, {
      params: { bank, bankCreditRequestId },
    });
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, bankCommentsAll, [bank, bankCreditRequestId], requestId);
  }
}

export async function bankCommentCreate(data: CreateBankCommentRequest) {
  const requestId = REQUEST_BANK_COMMENT_CREATE_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.post(API_V1_BANK_COMMENTS, data);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, bankCommentCreate, [data], requestId);
  }
}

export async function bankCommentReply(data: ReplyBankCommentRequest) {
  const requestId = REQUEST_BANK_COMMENT_REPLY_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.post(`${API_V1_BANK_COMMENTS}/reply`, data);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, bankCommentReply, [data], requestId);
  }
}

export async function bankNotesAll(bankCreditRequestId: number, bank?: Bank, noteType?: NoteType) {
  const requestId = bankNotesRequestId(bankCreditRequestId, bank, noteType);
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(API_V1_BANK_CREDIT_NOTES, {
      params: { bankCreditRequestId, bank, noteType },
    });
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, bankNotesAll, [bankCreditRequestId, bank, noteType], requestId);
  }
}

export async function bankNoteCreate(data: BankCreditNoteRequest) {
  const requestId = REQUEST_BANK_NOTE_CREATE_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.post(API_V1_BANK_CREDIT_NOTES, data);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, bankNoteCreate, [data], requestId);
  }
}

export async function bankNoteDelete(id: number) {
  const requestId = REQUEST_BANK_NOTE_DELETE_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.delete(`${API_V1_BANK_CREDIT_NOTES}/${id}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, bankNoteDelete, [id], requestId);
  }
}

export async function bankAttachmentsAll(bankCreditRequestId: number, bank: Bank) {
  const requestId = REQUEST_BANK_ATTACHMENT_LIST_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(API_V1_BANK_ATTACHMENTS, {
      params: { bankCreditRequestId, bank },
    });
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, bankAttachmentsAll, [bankCreditRequestId, bank], requestId);
  }
}

export async function bankAttachmentSummaryGet(bankCreditRequestId: number) {
  const requestId = REQUEST_BANK_ATTACHMENT_SUMMARY_GET_ID + bankCreditRequestId;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_BANK_ATTACHMENTS}/summary`, {
      params: { bankCreditRequestId },
    });
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, bankAttachmentSummaryGet, [bankCreditRequestId], requestId);
  }
}

export async function bankAttachmentSummaryUpload(bankCreditRequestId: number, file: File) {
  const requestId = REQUEST_BANK_ATTACHMENT_SUMMARY_UPLOAD_ID + bankCreditRequestId;
  try {
    store.dispatch(start(requestId));
    const formData = new FormData();
    formData.append("file", file);
    const response = await APIKit.post(`${API_V1_BANK_ATTACHMENTS}/summary`, formData, {
      params: { bankCreditRequestId },
      headers: { "Content-Type": "multipart/form-data" },
    });
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, bankAttachmentSummaryUpload, [bankCreditRequestId, file], requestId);
  }
}

export async function bankAttachmentUploadMain(bankCreditRequestId: number, bank: Bank, file: File) {
  const requestId = REQUEST_BANK_ATTACHMENT_UPLOAD_ID;
  try {
    store.dispatch(start(requestId));
    const formData = new FormData();
    formData.append("file", file);
    const response = await APIKit.post(`${API_V1_BANK_ATTACHMENTS}/main`, formData, {
      params: { bankCreditRequestId, bank },
      headers: { "Content-Type": "multipart/form-data" },
    });
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, bankAttachmentUploadMain, [bankCreditRequestId, bank, file], requestId);
  }
}

export async function bankAttachmentUploadMultiple(bankCreditRequestId: number, bank: Bank, files: File[]) {
  const requestId = REQUEST_BANK_ATTACHMENT_UPLOAD_ID;
  try {
    store.dispatch(start(requestId));
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    const response = await APIKit.post(`${API_V1_BANK_ATTACHMENTS}/multiple`, formData, {
      params: { bankCreditRequestId, bank },
      headers: { "Content-Type": "multipart/form-data" },
    });
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, bankAttachmentUploadMultiple, [bankCreditRequestId, bank, files], requestId);
  }
}

export async function bankAttachmentDelete(id: string) {
  const requestId = REQUEST_BANK_ATTACHMENT_DELETE_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.delete(`${API_V1_BANK_ATTACHMENTS}/${id}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, bankAttachmentDelete, [id], requestId);
  }
}

export async function bankAttachmentUploadZinz(bankCreditRequestId: number, bank: Bank, file: File) {
  const requestId = REQUEST_BANK_ATTACHMENT_UPLOAD_ID + "_ZINZ";
  try {
    store.dispatch(start(requestId));
    const formData = new FormData();
    formData.append("file", file);
    const response = await APIKit.post(`${API_V1_BANK_ATTACHMENTS}/zinz`, formData, {
      params: { bankCreditRequestId, bank },
      headers: { "Content-Type": "multipart/form-data" },
    });
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, bankAttachmentUploadZinz, [bankCreditRequestId, bank, file], requestId);
  }
}
